import React from "react";
import { useToast } from "@chakra-ui/react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchTenantRoles, fetchMenus, fetchRoleMenus, updateRoleMenus } from "action/menuAction";

/* ── Icons ── */
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

/* Checkbox with indeterminate support */
function IndeterminateCheckbox({ checked, indeterminate, onChange, label, depth = 0 }) {
  const ref = React.useRef();
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <label className="lms-menu-item" style={{ paddingLeft: depth > 0 ? 8 + depth * 20 : 8, flex: 1 }}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: 15, height: 15, flexShrink: 0, accentColor: "var(--brand-500)", cursor: "pointer" }}
      />
      <span style={{ fontSize: 13, color: "var(--text-base)" }}>{label}</span>
    </label>
  );
}

function TenantMenuSetting() {
  const { t } = useTranslation();
  React.useEffect(() => { document.title = t("Setting Menu Tenant") + " | LMS Tenant"; }, []);

  const { id: tenantId } = useParams();
  const history = useHistory();
  const toast = useToast();

  const [roles, setRoles] = React.useState([]);
  const [menus, setMenus] = React.useState([]);
  const [selectedMenusByRole, setSelectedMenusByRole] = React.useState({});
  const [activeRoleId, setActiveRoleId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const buildMenuTree = (flatMenus) => {
    const map = {};
    const roots = [];
    flatMenus.forEach((m) => { map[m.id] = { ...m, children: [] }; });
    flatMenus.forEach((m) => {
      if (m.parent_id && map[m.parent_id]) map[m.parent_id].children.push(map[m.id]);
      else roots.push(map[m.id]);
    });
    return roots;
  };

  const menuTree = React.useMemo(() => buildMenuTree(menus), [menus]);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [rolesResult, menusResult] = await Promise.all([fetchTenantRoles(tenantId), fetchMenus(tenantId)]);
        if (!rolesResult.success) { toast({ title: t("Error"), description: rolesResult.message, status: "error", duration: 3000, isClosable: true }); return; }
        if (!menusResult.success) { toast({ title: t("Error"), description: menusResult.message, status: "error", duration: 3000, isClosable: true }); return; }
        setRoles(rolesResult.data || []);
        setMenus(menusResult.data || []);
        const menuMap = {};
        await Promise.all(
          (rolesResult.data || []).map(async (role) => {
            const result = await fetchRoleMenus(tenantId, role.id);
            menuMap[role.id] = result.success ? result.data.map(String) : [];
          })
        );
        setSelectedMenusByRole(menuMap);
        if (rolesResult.data?.length > 0) setActiveRoleId(rolesResult.data[0].id);
      } catch {
        toast({ title: t("Error"), description: t("Gagal memuat data"), status: "error", duration: 3000, isClosable: true });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  const getDescendantIds = (node) => {
    let ids = [node.id];
    if (node.children?.length > 0) node.children.forEach((c) => { ids = ids.concat(getDescendantIds(c)); });
    return ids;
  };

  const handleToggleMenu = (roleId, menuId, descendants = []) => {
    const allIds = [menuId, ...descendants].map(String);
    setSelectedMenusByRole((prev) => {
      const current = prev[roleId] || [];
      const allChecked = allIds.every((id) => current.includes(id));
      return { ...prev, [roleId]: allChecked ? current.filter((id) => !allIds.includes(id)) : [...new Set([...current, ...allIds])] };
    });
  };

  const handleToggleAll = (roleId, allMenuIds) => {
    const current = selectedMenusByRole[roleId] || [];
    const allIds = allMenuIds.map(String);
    const allSelected = allIds.every((id) => current.includes(id));
    setSelectedMenusByRole((prev) => ({ ...prev, [roleId]: allSelected ? [] : allIds }));
  };

  const handleSave = async (roleId) => {
    setSaving(true);
    try {
      const menuIds = (selectedMenusByRole[roleId] || []).map(Number);
      const result = await updateRoleMenus(tenantId, roleId, menuIds);
      if (result.success) {
        toast({ title: t("Berhasil"), description: t("Setting menu berhasil disimpan"), status: "success", duration: 3000, isClosable: true });
      } else {
        toast({ title: t("Error"), description: result.message, status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("Gagal menyimpan"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(roles.map((role) => {
        const menuIds = (selectedMenusByRole[role.id] || []).map(Number);
        return updateRoleMenus(tenantId, role.id, menuIds);
      }));
      toast({ title: t("Berhasil"), description: t("Semua setting menu berhasil disimpan"), status: "success", duration: 3000, isClosable: true });
    } catch {
      toast({ title: t("Error"), description: t("Gagal menyimpan"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const renderMenuNode = (node, roleId, depth = 0) => {
    const selected = (selectedMenusByRole[roleId] || []).includes(String(node.id));
    const descendants = node.children?.length > 0 ? node.children.flatMap((c) => getDescendantIds(c)) : [];
    const hasChildren = node.children?.length > 0;
    const allChildrenSelected = hasChildren && descendants.every((id) => (selectedMenusByRole[roleId] || []).includes(String(id)));
    const someChildrenSelected = hasChildren && !allChildrenSelected && descendants.some((id) => (selectedMenusByRole[roleId] || []).includes(String(id)));

    return (
      <div key={node.id}>
        <IndeterminateCheckbox
          checked={selected && (!hasChildren || allChildrenSelected)}
          indeterminate={hasChildren && someChildrenSelected}
          onChange={() => handleToggleMenu(roleId, node.id, descendants)}
          label={node.name}
          depth={depth}
        />
        {node.children?.length > 0 && (
          <div>{node.children.map((child) => renderMenuNode(child, roleId, depth + 1))}</div>
        )}
      </div>
    );
  };

  const allMenuIds = menus.map((m) => m.id);
  const activeRole = roles.find((r) => r.id === activeRoleId);

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>{t("Master Tenant")}</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Setting Menu")}</span>
          </div>
          <div className="t-display">{t("Setting Menu Tenant")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
            <span className="t-small">{t("Konfigurasi akses menu per role.")}</span>
            <span style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 9px", background: "var(--brand-50)", color: "var(--brand-700)", borderRadius: 999, border: "1px solid var(--brand-100)" }}>
              ID #{tenantId}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={() => history.push("/admin/master-tenant")}>
            <ArrowLeftIcon /> {t("Kembali")}
          </button>
          <button className="lms-btn lms-btn-primary" onClick={handleSaveAll} disabled={saving}>
            {saving ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <SaveIcon />}
            {t("Simpan Semua")}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80, gap: 16 }}>
          <div className="lms-spinner" style={{ width: 32, height: 32 }} />
          <span className="t-body lms-text-muted">{t("Memuat data menu...")}</span>
        </div>
      ) : roles.length === 0 ? (
        <div className="lms-card">
          <div className="lms-card-body">
            <div className="lms-empty">
              <div className="lms-empty-icon"><MenuIcon /></div>
              <div className="t-h3">{t("Belum ada role untuk tenant ini")}</div>
              <div className="t-small">{t("Clone data mungkin belum berhasil. Coba kembali ke daftar tenant.")}</div>
              <button className="lms-btn lms-btn-primary" style={{ marginTop: 16 }} onClick={() => history.push("/admin/master-tenant")}>
                {t("Ke Daftar Tenant")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="lms-card" style={{ overflow: "hidden" }}>
          {/* Card header */}
          <div className="lms-card-header">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--brand-500)" }}><MenuIcon /></span>
              <div>
                <div className="t-h2" style={{ fontSize: 14.5 }}>{t("Konfigurasi Akses Menu")}</div>
                <div className="t-small" style={{ marginTop: 2 }}>{t("Pilih menu yang dapat diakses oleh setiap role pada tenant ini.")}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              <strong style={{ color: "var(--text-strong)" }}>{roles.length}</strong> role &nbsp;·&nbsp; <strong style={{ color: "var(--text-strong)" }}>{menus.length}</strong> menu
            </div>
          </div>

          {/* Two-pane layout */}
          <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", minHeight: 480 }}>

            {/* ── Role sidebar ── */}
            <div className="lms-role-sidebar">
              <div style={{ padding: "12px 18px 8px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                {t("Roles")}
              </div>
              {roles.map((role) => {
                const count = (selectedMenusByRole[role.id] || []).length;
                const isActive = activeRoleId === role.id;
                return (
                  <button
                    key={role.id}
                    className={`lms-role-item${isActive ? " active" : ""}`}
                    onClick={() => setActiveRoleId(role.id)}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: isActive ? 600 : 500, color: isActive ? "var(--brand-700)" : "var(--text-strong)", lineHeight: 1.3 }}>
                        {role.name}
                      </div>
                      {!role.is_active && (
                        <div style={{ fontSize: 11, color: "var(--danger-500)", marginTop: 2 }}>{t("Inactive")}</div>
                      )}
                    </div>
                    <span className="lms-role-item-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* ── Menu panel ── */}
            {activeRole && (() => {
              const sel = selectedMenusByRole[activeRole.id] || [];
              const selectedCount = sel.length;
              const allSelected = allMenuIds.length > 0 && allMenuIds.every((id) => sel.includes(String(id)));
              const someSelected = selectedCount > 0 && !allSelected;

              return (
                <div style={{ padding: 22, borderLeft: "1px solid var(--border-subtle)" }}>
                  {/* Role title row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--brand-50)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand-500)", flexShrink: 0 }}>
                      <ShieldIcon />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-strong)" }}>{activeRole.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>
                        {selectedCount} {t("dari")} {menus.length} {t("menu diaktifkan")}
                      </div>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                      <button
                        className="lms-btn lms-btn-secondary lms-btn-sm"
                        onClick={() => handleSave(activeRole.id)}
                        disabled={saving}
                      >
                        {saving ? <span className="lms-spinner" style={{ width: 12, height: 12 }} /> : <SaveIcon />}
                        {t("Simpan Role Ini")}
                      </button>
                    </div>
                  </div>

                  {/* Toolbar */}
                  <div className="lms-menu-toolbar">
                    <IndeterminateCheckbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => handleToggleAll(activeRole.id, allMenuIds)}
                      label={t("Pilih Semua Menu")}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{t("Terpilih:")}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 9px", background: "var(--brand-50)", color: "var(--brand-700)", borderRadius: 6, border: "1px solid var(--brand-100)" }}>
                        {selectedCount} / {menus.length}
                      </span>
                    </div>
                  </div>

                  {/* Menu grid */}
                  {menus.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                       <div className="t-small lms-text-muted">{t("Tidak ada menu tersedia.")}</div>
                    </div>
                  ) : (
                    <div className="lms-menu-tree">
                      {menuTree.map((node) => {
                        const descendants = node.children?.length > 0 ? node.children.flatMap((c) => getDescendantIds(c)) : [];
                        const allIds = [node.id, ...descendants].map(String);
                        const selCount = allIds.filter((id) => sel.includes(id)).length;
                        const hasSelection = selCount > 0;

                        const nodeChecked = sel.includes(String(node.id)) &&
                          (!node.children?.length || descendants.every((id) => sel.includes(String(id))));
                        const nodeIndeterminate = node.children?.length > 0 && (() => {
                          const allC = descendants.every((id) => sel.includes(String(id)));
                          return !allC && (sel.includes(String(node.id)) || descendants.some((id) => sel.includes(String(id))));
                        })();

                        return (
                          <div key={node.id} className={`lms-menu-group${hasSelection ? " has-selection" : ""}`}>
                            <div className="lms-menu-group-head">
                              <IndeterminateCheckbox
                                checked={nodeChecked}
                                indeterminate={nodeIndeterminate}
                                onChange={() => {
                                  const desc = node.children?.length > 0 ? node.children.flatMap((c) => getDescendantIds(c)) : [];
                                  handleToggleMenu(activeRole.id, node.id, desc);
                                }}
                                label={node.name}
                              />
                              <span className="lms-menu-group-count">
                                {node.children?.length > 0 ? `${selCount}/${allIds.length}` : (sel.includes(String(node.id)) ? "✓" : "—")}
                              </span>
                            </div>
                            {node.children?.length > 0 && (
                              <div className="lms-menu-items">
                                {node.children.map((child) => renderMenuNode(child, activeRole.id, 0))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantMenuSetting;
